import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStoreThunk } from '../features/stores/storeThunks';
import { fetchReportsThunk, fetchIncidentTypeCountsThunk } from '../features/reports/reportThunks';
import { fetchTransactionsThunk } from '../features/transactions/transactionThunks';
import IncentiveInfo from '../Component/IncentiveInfo';
import ReportStatistics from '../Component/ReportStatistics';
import Sidebar from '../Component/Sidebar';

// CSS for SingleStorePage with modern design + pagination
const styles = `
  /* Layout */
  .modern-store-page {
    display: flex;
    min-height: 100vh;
    background-color: #F3F4F6;
  }
  
  .modern-store-content {
    margin-left: 0;
    padding: 0;
    width: 80%;
  }
  
  .modern-container {
    max-width: 1440px;
    margin: 0 auto;
    padding: 32px 16px;
  }
  
  /* Header */
  .modern-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
    flex-wrap: wrap;
    gap: 16px;
  }
  
  .modern-header-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  
  .modern-back-btn {
    padding: 8px;
    border-radius: 50%;
    background-color: #2CC8DE;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .modern-back-btn:hover {
    background-color: #28b5c9;
  }
  
  .modern-back-btn i {
    color: white;
    font-size: 16px;
  }
  
  .modern-title {
    font-size: 24px;
    font-weight: 700;
    color: #2B3674;
    margin: 0;
  }
  
  .modern-header-right {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
  }
  
  .modern-date-btn {
    background-color: white;
    padding: 8px 16px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border: none;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: background-color 0.2s;
    white-space: nowrap;
  }
  
  .modern-date-btn:hover {
    background-color: #F9FAFB;
  }
  
  .modern-date-btn i {
    color: #2CC8DE;
  }
  
  .modern-date-btn span {
    color: #2B3674;
  }
  
  .modern-export-btn {
    background-color: #FF5BAA;
    color: white;
    padding: 8px 16px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    border: none;
    cursor: pointer;
    transition: background-color 0.2s;
    white-space: nowrap;
  }
  
  .modern-export-btn:hover {
    background-color: #ff4499;
  }
  
  /* Store and Chart Row */
  .modern-grid-row {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
    margin-bottom: 32px;
  }
  
  @media (min-width: 1024px) {
    .modern-grid-row {
      grid-template-columns: 1fr 1fr;
    }
  }
  
  /* Store Card */
  .modern-store-card {
    position: relative;
    height: 400px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 16px rgba(0,0,0,0.1);
  }
  
  .modern-store-card-skeleton {
    height: 400px;
    border-radius: 12px;
    background-color: #E5E7EB;
    animation: modern-pulse 2s infinite;
  }
  
  .modern-store-overlay {
    position: absolute;
    inset: 0;
    background: linear-gradient(to right, rgba(30, 58, 138, 0.8), rgba(67, 56, 202, 0.7));
    z-index: 10;
  }
  
  .modern-store-image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
  }
  
  .modern-store-info {
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 32px;
    z-index: 20;
    width: 100%;
  }
  
  .modern-store-info-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
  
  .modern-store-name {
    font-size: 36px;
    font-weight: 700;
    color: white;
    margin: 0 0 8px 0;
  }
  
  .modern-store-location {
    font-size: 20px;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
  }
  
  .modern-store-id {
    background-color: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(8px);
    padding: 8px 16px;
    border-radius: 8px;
  }
  
  .modern-store-id p {
    color: white;
    font-weight: 500;
    margin: 0;
  }
  
  .modern-store-error {
    height: 400px;
    border-radius: 12px;
    background-color: #E5E7EB;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6B7280;
  }
  
  /* Chart Container */
  .modern-chart-container {
    background-color: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  }
  
  .modern-chart-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
  }
  
  .modern-chart-title {
    font-size: 20px;
    font-weight: 700;
    color: #1F2937;
    margin: 0;
  }
  
  .modern-chart-tabs {
    display: flex;
    gap: 8px;
  }
  
  .modern-chart-tab {
    padding: 4px 12px;
    font-size: 14px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
  }
  
  .modern-chart-tab.active {
    background-color: #4F46E5;
    color: white;
  }
  
  .modern-chart-tab:not(.active) {
    background-color: #F3F4F6;
    color: #374151;
  }
  
  .modern-chart-tab:not(.active):hover {
    background-color: #E5E7EB;
  }
  
  .modern-chart-content {
    width: 100%;
    height: 320px;
  }
  
  /* Analytics Cards */
  .modern-analytics-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    margin-bottom: 32px;
  }
  
  @media (min-width: 768px) {
    .modern-analytics-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  
  @media (min-width: 1024px) {
    .modern-analytics-grid {
      grid-template-columns: repeat(5, 1fr);
    }
  }
  
  .modern-analytics-card {
    background-color: white;
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    transition: box-shadow 0.3s;
  }
  
  .modern-analytics-card:hover {
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }
  
  .modern-analytics-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  
  .modern-analytics-label {
    color: #6B7280;
    font-weight: 500;
    font-size: 14px;
    margin: 0;
  }
  
  .modern-analytics-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .modern-analytics-icon.green {
    background-color: #DCFCE7;
  }
  
  .modern-analytics-icon.green i {
    color: #16A34A;
  }
  
  .modern-analytics-icon.red {
    background-color: #FEE2E2;
  }
  
  .modern-analytics-icon.red i {
    color: #DC2626;
  }
  
  .modern-analytics-icon.blue {
    background-color: #DBEAFE;
  }
  
  .modern-analytics-icon.blue i {
    color: #2563EB;
  }
  
  .modern-analytics-icon.yellow {
    background-color: #FEF3C7;
  }
  
  .modern-analytics-icon.yellow i {
    color: #D97706;
  }
  
  .modern-analytics-icon.purple {
    background-color: #EDE9FE;
  }
  
  .modern-analytics-icon.purple i {
    color: #7C3AED;
  }
  
  .modern-analytics-value {
    font-size: 30px;
    font-weight: 700;
    color: #1F2937;
    margin: 0;
  }
  
  .modern-analytics-change {
    font-size: 14px;
    margin: 8px 0 0 0;
  }
  
  .modern-analytics-change.green {
    color: #16A34A;
  }
  
  .modern-analytics-change.red {
    color: #DC2626;
  }
  
  .modern-analytics-change.blue {
    color: #2563EB;
  }
  
  .modern-analytics-change.yellow {
    color: #D97706;
  }
  
  .modern-analytics-change.purple {
    color: #7C3AED;
  }

  /* Modern Table Styles (Dashboard-style) */
  .table-container {
    background-color: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    overflow: hidden;
    margin-bottom: 32px;
  }

  .table-header {
    padding: 20px 24px;
    background: none;
    border-bottom: 1px solid rgba(0,0,0,0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .table-header h2 {
    color: black;
    font-size: 18px;
    font-weight: 600;
    margin: 0;
  }

  .table-info {
    color: black;
    font-size: 14px;
    margin: 0;
  }

  .table-wrapper {
    overflow: hidden;
  }

  .table-scroll-container {
    overflow-x: auto;
    position: relative;
  }

  .modern-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    background-color: white;
  }

  .modern-table thead {
    background-color: #F8FAFC;
  }

  .modern-table th {
    padding: 16px 20px;
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: #64748B;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1px solid #E2E8F0;
    white-space: nowrap;
  }

  .modern-table tbody {
    background-color: white;
  }

  .modern-table td {
    padding: 16px 20px;
    border-bottom: 1px solid #F1F5F9;
    color: #1E293B;
    font-size: 14px;
    vertical-align: middle;
  }

  .modern-table tbody tr {
    transition: background-color 0.2s ease;
    cursor: pointer;
  }

  .modern-table tbody tr:hover {
    background-color: #F8FAFC;
  }

  .modern-table tbody tr:last-child td {
    border-bottom: none;
  }

  /* Store/Employee Info Cells */
  .store-info, .creator-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .store-image-container, .creator-image-container {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    overflow: hidden;
    flex-shrink: 0;
  }

  .store-image, .creator-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .store-name, .creator-name {
    font-weight: 500;
    color: #1E293B;
  }

  /* Date Info */
  .date-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .date-primary {
    font-weight: 500;
    color: #1E293B;
  }

  .date-secondary {
    font-size: 12px;
    color: #64748B;
  }

  /* Status Badges */
  .severity-badge, .status-badge, .involves-badge {
    display: inline-flex;
    align-items: center;
    padding: 4px 12px;
    font-size: 12px;
    font-weight: 500;
    border-radius: 6px;
    text-transform: capitalize;
  }

  .severity-badge.high {
    background-color: #FEE2E2;
    color: #DC2626;
  }

  .severity-badge.medium {
    background-color: #FEF3C7;
    color: #D97706;
  }

  .severity-badge.low {
    background-color: #DCFCE7;
    color: #16A34A;
  }

  .status-badge.approved {
    background-color: #DCFCE7;
    color: #16A34A;
  }

  .status-badge.submitted_review {
    background-color: #DBEAFE;
    color: #2563EB;
  }

  .status-badge.submitted_approval {
    background-color: #FEF3C7;
    color: #D97706;
  }

  .status-badge.declined {
    background-color: #FEE2E2;
    color: #DC2626;
  }

  .status-badge.discrepancy {
    background-color: #F3E8FF;
    color: #7C3AED;
  }

  .involves-badge {
    background-color: #F1F5F9;
    color: #475569;
  }

  /* Reason Cell */
  .reason-cell {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 200px;
  }

  .reason-text {
    font-weight: 500;
    color: #1E293B;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .call-status {
    font-size: 12px;
    color: #64748B;
  }

  /* Action Buttons */
  .action-buttons {
    display: flex;
    gap: 8px;
  }

  .action-btn {
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;
  }

  .action-btn.edit {
    background-color: #3B82F6;
    color: white;
    border-color: #3B82F6;
  }

  .action-btn.edit:hover {
    background-color: #2563EB;
  }

  .action-btn.view {
    background-color: transparent;
    color: #64748B;
    border-color: #CBD5E1;
  }

  .action-btn.view:hover {
    background-color: #F8FAFC;
  }

  /* Transaction specific styles */
  .transaction-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }

  .transaction-id {
    font-weight: 600;
    color: #1E293B;
  }

  .transaction-description {
    font-size: 12px;
    color: #64748B;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
  }

  .device-info {
    display: flex;
    flex-direction: column;
  }

  .device-name {
    font-weight: 500;
    color: #1E293B;
  }

  /* Video thumbnail */
  .video-container {
    width: 60px;
    height: 40px;
    border-radius: 6px;
    overflow: hidden;
    position: relative;
    cursor: pointer;
  }

  .video-thumbnail {
    width: 100%;
    height: 100%;
    background-color: #F1F5F9;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease;
  }

  .video-thumbnail:hover {
    transform: scale(1.05);
  }

  .play-button {
    color: #64748B;
    font-size: 16px;
  }

  .no-video {
    width: 100%;
    height: 100%;
    background-color: #F8FAFC;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed #CBD5E1;
    border-radius: 6px;
  }

  .no-video-text {
    font-size: 10px;
    color: #94A3B8;
    text-align: center;
  }

  /* Loading states */
  .skeleton-row {
    height: 60px;
    background-color: #F1F5F9;
    border-radius: 8px;
    margin-bottom: 8px;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }

  /* Empty States */
  .modern-empty-state {
    padding: 64px 32px;
    text-align: center;
    color: #6B7280;
  }
  
  .modern-empty-state i {
    font-size: 48px;
    color: #D1D5DB;
    margin-bottom: 16px;
  }
  
  .modern-empty-state h3 {
    font-size: 18px;
    font-weight: 500;
    margin: 0 0 8px 0;
    color: #374151;
  }
  
  .modern-empty-state p {
    margin: 0;
  }

  /* Video Modal */
  .modern-video-modal {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  
  .modern-video-content {
    width: 80%;
    max-width: 1024px;
    position: relative;
  }
  
  .modern-video-player {
    width: 100%;
    border-radius: 8px;
  }
  
  .modern-video-close {
    position: absolute;
    top: -40px;
    right: 0;
    background: none;
    border: none;
    color: white;
    font-size: 24px;
    cursor: pointer;
    transition: color 0.2s;
  }
  
  .modern-video-close:hover {
    color: #D1D5DB;
  }

  /* Pagination Styles */
  .pagination-container {
    padding: 20px 24px;
    background-color: #F8FAFC;
    border-top: 1px solid #E2E8F0;
    display: flex;
    justify-content: between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
  }

  .pagination-info {
    color: #64748B;
    font-size: 14px;
    flex: 1;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pagination-btn {
    padding: 8px 12px;
    border: 1px solid #CBD5E1;
    background-color: white;
    color: #374151;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 14px;
    text-align: center;
  }

  .pagination-btn:hover:not(:disabled) {
    background-color: #F1F5F9;
    border-color: #94A3B8;
  }

  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .pagination-btn.active {
    background-color: #000;
    color: white;
    border-color: #000;
  }

  .pagination-btn.active:hover {
    background-color: #2c2c2cff;
  }

  .pagination-ellipsis {
    padding: 8px 4px;
    color: #64748B;
    font-size: 14px;
  }

  /* Responsive Pagination */
  @media (max-width: 640px) {
    .pagination-container {
      flex-direction: column;
      gap: 12px;
    }

    .pagination-info {
      text-align: center;
    }

    .pagination-controls {
      flex-wrap: wrap;
      justify-content: center;
    }

    .pagination-btn {
      min-width: 36px;
      padding: 6px 10px;
      font-size: 13px;
    }
  }
  
  /* Animations */
  @keyframes modern-pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
  
  /* Responsive */
  @media (max-width: 768px) {
    .modern-header {
      flex-direction: column;
      align-items: stretch;
    }
    
    .modern-header-right {
      justify-content: center;
    }
    
    .modern-store-name {
      font-size: 28px;
    }
    
    .modern-store-location {
      font-size: 16px;
    }
    
    .modern-store-info-content {
      flex-direction: column;
      gap: 16px;
      align-items: flex-start;
    }
  }
`;

const SingleStorePage = () => {
  const { storeId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get store info from Redux store
  const { currentStore, loading: storeLoading } = useSelector(state => state.stores);
  
  // Get reports for this store
  const { reports, loading: reportsLoading, incidentTypeCounts, count: reportsCount } = useSelector(state => state.reports);
  
  // Get transactions for this store
  const { transactions, loading: transactionsLoading, count: transactionsCount } = useSelector(state => state.transactions);
  
  // Local pagination state
  const [reportsCurrentPage, setReportsCurrentPage] = useState(1);
  const [transactionsCurrentPage, setTransactionsCurrentPage] = useState(1);
  const itemsPerPage = 10; // Show 10 items per page
  
  // Manage modal state for video preview
  const [videoPreview, setVideoPreview] = useState(null);
  
  useEffect(() => {
    // Fetch store data
    dispatch(fetchStoreThunk(storeId));
    
    // Fetch incident type counts for this store
    dispatch(fetchIncidentTypeCountsThunk({ store_id: storeId }));
    
    // Load initial data
    loadReportsPage(1);
    loadTransactionsPage(1);
  }, [dispatch, storeId]);

  const loadReportsPage = (page) => {
    const offset = (page - 1) * itemsPerPage;
    dispatch(fetchReportsThunk({ 
      store_id: storeId, 
      offset, 
      limit: itemsPerPage 
    }));
    setReportsCurrentPage(page);
  };

  const loadTransactionsPage = (page) => {
    const offset = (page - 1) * itemsPerPage;
    dispatch(fetchTransactionsThunk({ 
      store_id: storeId, 
      offset, 
      limit: itemsPerPage 
    }));
    setTransactionsCurrentPage(page);
  };

  // Helper function to format 24-hour time to 12-hour format
  const formatIncidentTime = (timeString) => {
    if (!timeString) return 'N/A';
    
    try {
      const [hours, minutes] = timeString.split(':');
      const hour24 = parseInt(hours, 10);
      const minute = parseInt(minutes, 10);
      
      const period = hour24 >= 12 ? 'PM' : 'AM';
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      
      const formattedMinutes = minute.toString().padStart(2, '0');
      
      return `${hour12}:${formattedMinutes} ${period}`;
    } catch (error) {
      console.error('Error formatting incident time:', error);
      return 'N/A';
    }
  };

  // Helper function to get employee full name
  const getEmployeeFullName = (employee) => {
    if (!employee) return 'Unknown Employee';
    
    if (employee.name) return employee.name;
    
    if (employee.user) {
      const firstName = employee.user.first_name || '';
      const lastName = employee.user.last_name || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown Employee';
    }
    
    return 'Unknown Employee';
  };

  // Helper function to get store image URL or fallback to initial
  const getStoreImageUrl = (store) => {
    if (store?.image) {
      return store.image;
    }
    return `/api/placeholder/40/40?text=${store?.store_name?.charAt(0) || 'S'}`;
  };

  // Helper function to get employee image URL or fallback to initial
  const getEmployeeImageUrl = (employee) => {
    if (employee?.image) {
      return employee.image;
    }
    const name = getEmployeeFullName(employee);
    return `/api/placeholder/40/40?text=${name.charAt(0) || 'U'}`;
  };
  
  // Format date for the tables
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };
  
  // Format time for the tables
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };
  
  // Handle video preview
  const handleVideoPreview = (videoUrl) => {
    setVideoPreview(videoUrl);
  };
  
  // Close video preview
  const closeVideoPreview = () => {
    setVideoPreview(null);
  };

  // Navigation handlers
  const handleReportClick = (reportId) => {
    navigate(`/reports/${reportId}/edit`);
  };

  const handleTransactionClick = (transactionId) => {
    navigate(`/transaction/${transactionId}`);
  };

  // Fixed incident count function to match API response structure
  const getIncidentCount = (type) => {
    const counts = incidentTypeCounts?.counts;
    
    if (!counts || typeof counts !== 'object') {
      return 0;
    }
    
    const typeMapping = {
      'theft prevented': 'theftPrevented',
      'theft reported': 'theftReported', 
      'incident': 'incidentReported',
      'customer denied': 'customerDenied',
      'cashier suspicious': 'cashierSuspicious'
    };
    
    const apiKey = typeMapping[type.toLowerCase()];
    return counts[apiKey] || 0;
  };

  // Pagination component
  const PaginationControls = ({ currentPage, totalCount, itemsPerPage, onPageChange, loading }) => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalCount);

    const getVisiblePages = () => {
      const delta = 2;
      const range = [];
      const rangeWithDots = [];

      for (let i = Math.max(2, currentPage - delta); i <= Math.min(totalPages - 1, currentPage + delta); i++) {
        range.push(i);
      }

      if (currentPage - delta > 2) {
        rangeWithDots.push(1, '...');
      } else {
        rangeWithDots.push(1);
      }

      rangeWithDots.push(...range);

      if (currentPage + delta < totalPages - 1) {
        rangeWithDots.push('...', totalPages);
      } else if (totalPages > 1) {
        rangeWithDots.push(totalPages);
      }

      return rangeWithDots;
    };

    if (totalPages <= 1) return null;

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Showing {startItem} to {endItem} of {totalCount} entries
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1 || loading}
          >
            ‹ Previous
          </button>

          {getVisiblePages().map((page, index) => (
            page === '...' ? (
              <span key={index} className="pagination-ellipsis">...</span>
            ) : (
              <button
                key={index}
                className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                onClick={() => onPageChange(page)}
                disabled={loading}
              >
                {page}
              </button>
            )
          ))}

          <button
            className="pagination-btn"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages || loading}
          >
            Next ›
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <style>{styles}</style>
      <div className="index-box">
        <Sidebar />
        
        <div className="modern-store-content">
          <div className="modern-container">
            {/* Header */}
            <header className="modern-header">
              <div className="modern-header-left">
                <button className="modern-back-btn" onClick={() => navigate('/stores')}>
                  <i className="fas fa-arrow-left"></i>
                </button>
                <h1 className="modern-title">
                  {currentStore?.store_name || 'Store Analytics'}
                </h1>
              </div>
              <div className="modern-header-right">
                <button className="modern-date-btn">
                  <i className="fas fa-calendar"></i>
                  <span>June 1 - June 7, 2025</span>
                </button>
                <button className="modern-export-btn">
                  <i className="fas fa-download"></i> Export Data
                </button>
              </div>
            </header>

            {/* Store and Chart Row */}
            <div className="modern-grid-row">
              {/* Store Card */}
              {storeLoading ? (
                <div className="modern-store-card-skeleton"></div>
              ) : currentStore ? (
                <div className="modern-store-card">
                  <div className="modern-store-overlay"></div>
                  <img
                    src={currentStore.image || "https://readdy.ai/api/search-image?query=Modern%20retail%20store%20interior%20with%20elegant%20displays%2C%20soft%20lighting%2C%20spacious%20aisles%2C%20and%20stylish%20fixtures.%20High-end%20shopping%20environment%20with%20minimalist%20design%2C%20clean%20lines%2C%20and%20premium%20merchandise%20presentation.%20Professional%20retail%20photography%20with%20depth%20of%20field%20and%20balanced%20composition&width=1440&height=400&seq=store1&orientation=landscape"}
                    alt="Store Interior"
                    className="modern-store-image"
                  />
                  <div className="modern-store-info">
                    <div className="modern-store-info-content">
                      <div>
                        <h2 className="modern-store-name">{currentStore.store_name}</h2>
                        <p className="modern-store-location">
                          {currentStore.store_city || "City"}, {currentStore.store_state || "State"}
                        </p>
                      </div>
                      <div className="modern-store-id">
                        <p>Store ID: {currentStore.id}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="modern-store-error">
                  <p>Store not found</p>
                </div>
              )}

              {/* Chart */}
              <div className="modern-chart-container">
                <div className="modern-chart-header">
                  <h2 className="modern-chart-title">Weekly Trend Analysis</h2>
                  <div className="modern-chart-tabs">
                    <button className="modern-chart-tab">Daily</button>
                    <button className="modern-chart-tab active">Weekly</button>
                    <button className="modern-chart-tab">Monthly</button>
                  </div>
                </div>
                <div className="modern-chart-content">
                  <ReportStatistics employeeId={null} storeId={storeId} />
                </div>
              </div>
            </div>

            {/* Analytics Cards */}
            <div className="modern-analytics-grid">
              <div className="modern-analytics-card">
                <div className="modern-analytics-header">
                  <h3 className="modern-analytics-label">Theft Prevented</h3>
                  <div className="modern-analytics-icon green">
                    <i className="fas fa-shield-alt"></i>
                  </div>
                </div>
                <p className="modern-analytics-value">{getIncidentCount('theft prevented')}</p>
                <p className="modern-analytics-change green">
                  <i className="fas fa-arrow-up"></i> Updated
                </p>
              </div>

              <div className="modern-analytics-card">
                <div className="modern-analytics-header">
                  <h3 className="modern-analytics-label">Theft Reported</h3>
                  <div className="modern-analytics-icon red">
                    <i className="fas fa-exclamation-triangle"></i>
                  </div>
                </div>
                <p className="modern-analytics-value">{getIncidentCount('theft reported')}</p>
                <p className="modern-analytics-change red">
                  <i className="fas fa-arrow-up"></i> Updated
                </p>
              </div>

              <div className="modern-analytics-card">
                <div className="modern-analytics-header">
                  <h3 className="modern-analytics-label">Incident Reported</h3>
                  <div className="modern-analytics-icon blue">
                    <i className="fas fa-clipboard-list"></i>
                  </div>
                </div>
                <p className="modern-analytics-value">{getIncidentCount('incident')}</p>
                <p className="modern-analytics-change blue">
                  <i className="fas fa-arrow-down"></i> Updated
                </p>
              </div>

              <div className="modern-analytics-card">
                <div className="modern-analytics-header">
                  <h3 className="modern-analytics-label">Customer Denied</h3>
                  <div className="modern-analytics-icon yellow">
                    <i className="fas fa-user-times"></i>
                  </div>
                </div>
                <p className="modern-analytics-value">{getIncidentCount('customer denied')}</p>
                <p className="modern-analytics-change yellow">
                  <i className="fas fa-equals"></i> Updated
                </p>
              </div>

              <div className="modern-analytics-card">
                <div className="modern-analytics-header">
                  <h3 className="modern-analytics-label">Cashier Suspicious</h3>
                  <div className="modern-analytics-icon purple">
                    <i className="fas fa-eye"></i>
                  </div>
                </div>
                <p className="modern-analytics-value">{getIncidentCount('cashier suspicious')}</p>
                <p className="modern-analytics-change purple">
                  <i className="fas fa-arrow-down"></i> Updated
                </p>
              </div>
            </div>

            {/* Tables Section with Pagination */}
            <div className="modern-tables-section">
              {/* Transactions Table */}
              <div className="table-container">
                <div className="table-header">
                  <h2>Recent Transactions</h2>
                  <p className="table-info">
                    {transactionsLoading ? 'Loading...' : `${transactionsCount} total transactions`}
                  </p>
                </div>
                <div className="table-wrapper">
                  {transactionsLoading ? (
                    <div className="modern-table-loading">
                      <div className="skeleton-row"></div>
                      <div className="skeleton-row"></div>
                      <div className="skeleton-row"></div>
                    </div>
                  ) : transactions && transactions.length > 0 ? (
                    <>
                      <div className="table-scroll-container">
                        <table className="modern-table">
                          <thead>
                            <tr>
                              <th>Store</th>
                              <th>Transaction</th>
                              <th>Date</th>
                              <th>Device</th>
                              <th>Status</th>
                              <th>Video</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transactions.map((transaction) => (
                              <tr key={transaction.id} onClick={() => handleTransactionClick(transaction.id)}>
                                <td>
                                  <div className="store-info">
                                    <div className="store-image-container">
                                      <img 
                                        src={getStoreImageUrl(transaction.store)}
                                        alt={transaction.store?.store_name || "Store"}
                                        className="store-image"
                                      />
                                    </div>
                                    <div>
                                      <div className="store-name">{transaction.store?.store_name || currentStore?.store_name}</div>
                                      <div className="date-secondary">{transaction.store?.store_city || currentStore?.store_city}</div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div className="transaction-header">
                                    <span className="transaction-id">#{transaction.transaction_id || transaction.id}</span>
                                    {transaction.flagged && (
                                      <span className="severity-badge high">Alert</span>
                                    )}
                                  </div>
                                  <div className="transaction-description">{transaction.description || transaction.content || 'Standard Transaction'}</div>
                                </td>
                                <td>
                                  <div className="date-info">
                                    <span className="date-primary">{formatDate(transaction.date)}</span>
                                    <span className="date-secondary">{formatTime(transaction.date)}</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="device-info">
                                    <span className="device-name">{transaction.device || 'POS Terminal'}</span>
                                  </div>
                                </td>
                                <td>
                                  <span className={`status-badge ${
                                    transaction.flagged ? 'declined' :
                                    transaction.verified ? 'approved' :
                                    'submitted_review'
                                  }`}>
                                    {transaction.flagged ? 'Flagged' : 
                                     transaction.verified ? 'Verified' : 
                                     'Pending'}
                                  </span>
                                </td>
                                <td>
                                  <div className="video-container">
                                    {transaction.video_evidence ? (
                                      <div className="video-thumbnail" onClick={(e) => {
                                        e.stopPropagation();
                                        handleVideoPreview(transaction.video_evidence);
                                      }}>
                                        <div className="play-button">▶</div>
                                      </div>
                                    ) : (
                                      <div className="no-video">
                                        <span className="no-video-text">No Video</span>
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <PaginationControls
                        currentPage={transactionsCurrentPage}
                        totalCount={transactionsCount}
                        itemsPerPage={itemsPerPage}
                        onPageChange={loadTransactionsPage}
                        loading={transactionsLoading}
                      />
                    </>
                  ) : (
                    <div className="modern-empty-state">
                      <i className="fas fa-credit-card"></i>
                      <h3>No Transactions Available</h3>
                      <p>There are no transactions available for this store at the moment.</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Reports Table */}
              <div className="table-container">
                <div className="table-header">
                  <h2>Recent Reports</h2>
                  <p className="table-info">
                    {reportsLoading ? 'Loading...' : `${reportsCount} total reports`}
                  </p>
                </div>
                <div className="table-wrapper">
                  {reportsLoading ? (
                    <div className="modern-table-loading">
                      <div className="skeleton-row"></div>
                      <div className="skeleton-row"></div>
                      <div className="skeleton-row"></div>
                    </div>
                  ) : reports && reports.length > 0 ? (
                    <>
                      <div className="table-scroll-container">
                        <table className="modern-table">
                          <thead>
                            <tr>
                              <th>Business</th>
                              <th>Report Date</th>
                              <th>Created By</th>
                              <th>Incident Type</th>
                              <th>Severity</th>
                              <th>Status</th>
                              <th>Reason</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reports.map((report) => (
                              <tr key={report.id} onClick={() => handleReportClick(report.id)}>
                                <td>
                                  <div className="store-info">
                                    <div className="store-image-container">
                                      <img 
                                        src={getStoreImageUrl(report.store || currentStore)}
                                        alt={report.store?.store_name || currentStore?.store_name || "Store"}
                                        className="store-image"
                                      />
                                    </div>
                                    <span className="store-name">{report.store?.store_name || currentStore?.store_name}</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="date-info">
                                    <span className="date-primary">{report.incident_date}</span>
                                    <span className="date-secondary">{formatIncidentTime(report.incident_time)}</span>
                                  </div>
                                </td>
                                <td>
                                  <div className="creator-info">
                                    <div className="creator-image-container">
                                      <img 
                                        src={getEmployeeImageUrl(report.employee)}
                                        alt={getEmployeeFullName(report.employee)}
                                        className="creator-image"
                                      />
                                    </div>
                                    <span className="creator-name">{getEmployeeFullName(report.employee)}</span>
                                  </div>
                                </td>
                                <td>
                                  <span className="incident-type">
                                    {report.incident_type}
                                  </span>
                                </td>
                                <td>
                                  <span className={`severity-badge ${report.severity?.toLowerCase() || 'low'}`}>
                                    {report.severity || 'Low'}
                                  </span>
                                </td>
                                <td>
                                  <span className={`status-badge ${report.status?.toLowerCase()}`}>
                                    {report.status}
                                  </span>
                                </td>
                                <td>
                                  <div className="reason-cell">
                                    <span className="reason-text">
                                      {report.call_status === 'CALLED'
                                        ? `${report.reason_called}`
                                        : `${report.reason_not_called}`}
                                    </span>
                                    <span className="call-status">
                                      {report.call_status === 'CALLED' ? 'Called' : 'Not Called'}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div className="action-buttons">
                                    <button className="action-btn edit" onClick={(e) => {
                                      e.stopPropagation();
                                      handleReportClick(report.id);
                                    }}>
                                      <span>Edit</span>
                                    </button>
                                    <button className="action-btn view" onClick={(e) => {
                                      e.stopPropagation();
                                      handleReportClick(report.id);
                                    }}>
                                      <span>View</span>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <PaginationControls
                        currentPage={reportsCurrentPage}
                        totalCount={reportsCount}
                        itemsPerPage={itemsPerPage}
                        onPageChange={loadReportsPage}
                        loading={reportsLoading}
                      />
                    </>
                  ) : (
                    <div className="modern-empty-state">
                      <i className="fas fa-clipboard-list"></i>
                      <h3>No Reports Available</h3>
                      <p>There are no reports available for this store at the moment.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Video Preview Modal */}
            {videoPreview && (
              <div className="modern-video-modal" onClick={closeVideoPreview}>
                <div className="modern-video-content" onClick={e => e.stopPropagation()}>
                  <video
                    src={videoPreview}
                    controls
                    autoPlay
                    className="modern-video-player"
                  />
                  <button
                    onClick={closeVideoPreview}
                    className="modern-video-close"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default SingleStorePage;